import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import type { Dispatch, SetStateAction } from 'react';
import type { SnackbarAction } from 'notistack';

import type { Id } from '@e-socna/data-layer';

import { NotiStackButton } from '../../../components/NotiStackButton';
import { PATH_NOT_FOUND } from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { memoForm2ApiMapper } from '../../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { EditorModePaths } from '../../../components/DocumentEditor/types/props.type';
import type { MemosState } from '../../../forms/MemoBeforeSubmit';
import type { BackError } from '../../../utils/back-errors.util';

import { sectionTemplateForm2ApiMapper } from '../../NewSectionTemplate/mappers/section-template.form2api.mapper';
import { MessageList } from '../../../components/MessageList';

import { useQueryInvalidation } from './useQueryInvalidation.hook';
import { useFoapuSectionTemplateMutation } from './useUpdateMutation.hook';
import type { SectionTemplatePage_OneSectionTemplate } from './useFindOneSectionTemplateQuery.hook';
import type { SectionTemplate_Foapu_Result } from './useUpdateMutation.hook';

interface Params {
  readonly sectionTemplateId: Id;
  readonly editorModePaths: EditorModePaths;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const useSaveHandler = ({
  sectionTemplateId,
  editorModePaths,
  setIsLoading,
}: Readonly<Params>) => {
  const { invalidateQueries } = useQueryInvalidation(sectionTemplateId);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { mutate: updateSectionTemplateMutation } =
    useFoapuSectionTemplateMutation();

  const onSave = useCallback(
    (
      values: SectionTemplatePage_OneSectionTemplate,
      memos: MemosState,
      handleSuccess?: (data: SectionTemplate_Foapu_Result) => void,
      handleError?: () => void,
      // eslint-disable-next-line max-params
    ) => {
      setIsLoading(true);

      const onSuccess = (data: SectionTemplate_Foapu_Result) => {
        setIsLoading(false);

        enqueueSnackbar('Votre modèle de section a bien été mis à jour.');

        invalidateQueries();

        handleSuccess?.(data);
      };

      const onError = (error: unknown) => {
        setIsLoading(false);
        const backError = error as BackError;
        let action: SnackbarAction | null = null;

        const supportedErrors = [
          'NO_CODE_OR_LABEL',
          'CODE_ALREADY_USED',
          'EMPTY_SECTION',
        ];

        const supportedErrorCode = backError.response.errors.find(
          (error) =>
            error.extensions.errCode &&
            supportedErrors.includes(error.extensions.errCode),
        )?.extensions.errCode;

        if (supportedErrorCode) {
          let to = editorModePaths.Editor;

          switch (supportedErrorCode) {
            case 'NO_CODE_OR_LABEL':
              to = editorModePaths.InfoEdit;
              break;
            case 'CODE_ALREADY_USED':
              to = editorModePaths.InfoEdit;
              break;
            case 'EMPTY_SECTION':
              to = editorModePaths.Editor;
              break;
            default:
              console.warn(
                `Unknown error code in SectionTemplate useSaveHandler: ${supportedErrorCode}`,
              );
          }

          action = (snackbarId) => (
            <NotiStackButton
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => {
                void navigate(to ?? PATH_NOT_FOUND);
                closeSnackbar(snackbarId);
              }}
              variant="text"
            >
              Modifier
            </NotiStackButton>
          );
        }

        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
            action,
          },
        );

        handleError?.();
      };

      updateSectionTemplateMutation(
        {
          filter: { _id: sectionTemplateId },
          update: sectionTemplateForm2ApiMapper(values),
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError,
          onSuccess,
        },
      );
    },
    [
      closeSnackbar,
      editorModePaths.Editor,
      editorModePaths.InfoEdit,
      enqueueSnackbar,
      invalidateQueries,
      navigate,
      sectionTemplateId,
      updateSectionTemplateMutation,
      setIsLoading,
    ],
  );

  return {
    onSave,
  };
};
